import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { ReactNode, useMemo } from 'react';

import { SymbolDisplay } from '@flipster/features/src/misc/display';
import { FormattedMessage } from '@flipster/i18n';
import { useOverlay } from '@flipster/overlay';
import { Button } from '@flipster/react/design-system/button';
import { TextButton } from '@flipster/react/design-system/text-button';
import { useIsElementInViewport } from '@flipster/react/misc/browser/intersection';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, vstack } from '@flipster/styled-system/patterns';
import LinkSVG from '@flipster/ui/assets/svgs/link.svg';

import TokenInfoModal from './TokenInfoModal';
import TokenStats from './TokenStats';
import { tokenInfoBunja } from './state';

const TokenInfo = () => {
  const t = useTranslations();
  const overlay = useOverlay();
  const [bottomRef, isVisibleBottom] = useIsElementInViewport<HTMLDivElement>();

  const { symbol, currencyAtom, marketStatsAtom, isRwaAtom, cryptocurrencyInfoAtom, hasReadMoreAtom } =
    useBunja(tokenInfoBunja);
  const currency = useAtomValue(currencyAtom);
  const marketStats = useAtomValue(marketStatsAtom);
  const isRwa = useAtomValue(isRwaAtom);
  const cryptocurrencyInfo = useAtomValue(cryptocurrencyInfoAtom);
  const hasReadMore = useAtomValue(hasReadMoreAtom);

  const clampedNodes = useMemo(() => {
    if (!cryptocurrencyInfo) {
      return marketStats?.intro ?? null;
    }
    const rawNodes = documentToReactComponents(cryptocurrencyInfo.description?.json);
    return Array.isArray(rawNodes) ? (rawNodes[0] as ReactNode) : rawNodes;
  }, [cryptocurrencyInfo, marketStats]);

  const openModal = () => {
    overlay.open(({ isOpen, onClose, unmount }) => (
      <TokenInfoModal
        isOpen={isOpen}
        onClose={onClose}
        content={documentToReactComponents(cryptocurrencyInfo?.description?.json)}
        onExit={unmount}
      />
    ));
  };

  return (
    <div className={css({ pos: 'relative', overflowY: 'scroll' })}>
      <div
        className={css(
          vstack.raw({
            p: 16,
            gap: 20,
            color: 'content.default.level-5',
            alignItems: 'stretch',
          }),
        )}
      >
        <header
          className={flex({
            justify: 'space-between',
            gap: 16,
          })}
        >
          <SymbolDisplay
            symbol={symbol}
            icon={<SymbolDisplay.Icon size={24} space={6} />}
            mainLabel={<SymbolDisplay.Label value='fullName' />}
            css={{ textStyle: 'Title/M' }}
          />
          {cryptocurrencyInfo && (
            <ul className={hstack({ gap: '8', flexWrap: 'wrap' })}>
              {cryptocurrencyInfo.linksCollection?.items.map((item) => {
                if (!item?.name || !item?.url) return null;
                return (
                  <li key={item.name + item.url}>
                    <Button
                      as='a'
                      color='neutral'
                      hierarchy='secondary'
                      href={item.url}
                      icon={<LinkSVG width={16} height={16} />}
                      label={item.name}
                      size='small'
                      target='_blank'
                    />
                  </li>
                );
              })}
            </ul>
          )}
        </header>

        {!isRwa && marketStats && currency && <TokenStats currency={currency} stats={marketStats} />}

        {clampedNodes && (
          <dl
            className={css({
              pt: 20,
              borderTopWidth: '1px',
              borderTopStyle: 'solid',
              borderColor: 'border.default.level-2',
            })}
          >
            <dt className={css({ textStyle: 'Title/XS', mb: 8 })}>
              <FormattedMessage id='Autogen_common.r251126.s_About' />
            </dt>
            <dd
              className={css({
                textStyle: 'Text/M/Regular',
                color: 'content.default.level-3',
                '& > p:not(:last-child)': { mb: '1.2em' },
              })}
            >
              {clampedNodes}
            </dd>
            {hasReadMore && (
              <TextButton label={t('Autogen_common.r251126.s_Read_more')} color='accent' onClick={openModal} />
            )}
          </dl>
        )}
        <div ref={bottomRef} />
      </div>
      {!isVisibleBottom && (
        <div
          className={css({
            pos: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            h: 40,
            pointerEvents: 'none',
            bg: 'linear-gradient(to bottom, transparent, {colors.background.default.level-0})',
          })}
        />
      )}
    </div>
  );
};

export default TokenInfo;
